import React, { ReactElement } from 'react';

import { Skeleton, Tooltip } from 'antd';
import {
  Column,
  Row,
  Text,
  Space,
  Avatar,
  PhoneOutlined,
  MailOutlined,
} from 'farmhub-ui-core';
import styled from 'styled-components';

import { InfoCard } from 'components';
import {
  EntityRoleOutlineIcon,
  LocationIcon,
  WebsiteOutlineIcon,
} from 'components/Icon';
import { theme } from 'styled';
import {
  DOMAIN_STATUS,
  formatImageUrl,
  getFullAddress,
  handleRedirect,
  IMAGETYPE,
} from 'utils';

type PartnerNoteItemProps = {
  data: any;
  children: ReactElement;
  partnerCode?: string;
  isLoading: boolean;
};

const BasicInfo = ({
  data,
  children,
  partnerCode,
  isLoading = false,
}: PartnerNoteItemProps) => {
  const { entityOptions } = data;

  return (
    <>
      {isLoading ? (
        <BasicInfoSkeleton />
      ) : (
        <>
          <InfoCartWrapper>
            <InfoCard
              img={
                <AvatarWrapper justifyContent="center">
                  <Avatar
                    size={64}
                    src={formatImageUrl(data.avatar, IMAGETYPE.AVATAR.xxs)}
                    styles={{ 'flex-shrink': 0 }}
                  />
                </AvatarWrapper>
              }
              info={
                <Column>
                  {data?.owner?.name ? (
                    <Text color="secondary" lineHeight="16px" size="xxs">
                      @{data?.owner?.name}
                    </Text>
                  ) : (
                    <></>
                  )}
                  <Space height={4} />
                  <Row alignItems="flex-start">
                    <Column
                      style={{ height: 'fit-content', width: 'fit-content' }}
                    >
                      <Space height={2} />
                      <LocationIcon
                        color={theme.colors.primary.main}
                        height={14}
                        width={14}
                      />
                    </Column>

                    <Space width={6} />
                    <Text color="secondary" size="xxs">
                      {getFullAddress(
                        data?.address,
                        data?.ward?.district?.fullName,
                        data?.ward?.province?.fullName,
                        data?.ward?.fullName
                      )}
                    </Text>
                  </Row>
                </Column>
              }
              name={
                <Row>
                  <Text color="dark" size="sm" weight="extraBold">
                    {data.name}
                  </Text>
                </Row>
              }
              width="100%"
            />
          </InfoCartWrapper>
          <Space height={18} />
          <Text color="primary" size="xs" weight="medium">
            Thông tin cơ bản:
          </Text>
          <Space height={12} />
          <Row>
            <Column>
              <Row>
                <IconWrapper justifyContent="center">
                  <EntityRoleOutlineIcon height={18} width={18} />
                </IconWrapper>
                <Space width={12} />
                <Column>
                  <Text color="secondary" size="tiny" weight="medium">
                    Mã đối tác
                  </Text>
                  <Row>
                    <Text color="main" size="xxs" weight="medium">
                      {partnerCode}
                    </Text>
                    <Space width={3} />
                    {children}
                  </Row>
                </Column>
              </Row>
              <Space height={18} />

              <RowInfo
                icon={
                  <PhoneOutlined
                    className="phone-out-line"
                    height={16}
                    width={16}
                  />
                }
                name={data?.phoneNumber}
                title="Số điện thoại"
              />
            </Column>
            <Column>
              <Row>
                <IconWrapper justifyContent="center">
                  <WebsiteOutlineIcon height={18} width={18} />
                </IconWrapper>
                <Space width={12} />
                <Column>
                  <Text color="secondary" size="tiny" weight="medium">
                    Website
                  </Text>
                  <Tooltip
                    title={
                      data?.domain &&
                      data.domainStatus == DOMAIN_STATUS.ACTIVATE
                        ? data?.domain
                        : data.website
                    }
                  >
                    <Text
                      color="main"
                      lineNumber={1}
                      size="xxs"
                      styles={{
                        textDecoration: 'underline',
                        textUnderlineOffset: '3px',
                        cursor: 'pointer',
                      }}
                      weight="medium"
                      onClick={e => {
                        handleRedirect(
                          data?.domain &&
                            data.domainStatus == DOMAIN_STATUS.ACTIVATE
                            ? data?.domain
                            : data.website
                        );
                        e.stopPropagation();
                      }}
                    >
                      {data?.domain &&
                      data.domainStatus == DOMAIN_STATUS.ACTIVATE
                        ? data?.domain
                        : data.website}
                    </Text>
                  </Tooltip>
                </Column>
              </Row>
              <Space height={18} />

              <RowInfo
                icon={<MailOutlined height={18} width={18} />}
                name={data.email}
                title="Email"
              />
            </Column>
          </Row>
        </>
      )}
    </>
  );
};

export default BasicInfo;

const RowInfo = ({
  icon,
  title,
  name,
}: {
  icon: ReactElement;
  title: string;
  name: string;
}) => (
  <Row>
    <IconWrapper justifyContent="center">{icon}</IconWrapper>
    <Space width={12} />
    <Column>
      <Text color="secondary" size="tiny" weight="medium">
        {title}
      </Text>
      <Text color="main" size="xxs" weight="medium">
        {name}
      </Text>
    </Column>
  </Row>
);

const BasicInfoSkeleton = () => (
  <>
    <InfoCartWrapper>
      <InfoCard
        img={<Skeleton.Avatar style={{ width: 64, height: 64 }} />}
        info={
          <Column>
            <Skeleton.Input size="small" style={{ width: '200px' }} />
            <Space height={4} />
            <Skeleton.Input size="small" style={{ width: '250px' }} />
          </Column>
        }
        name={
          <Row>
            <Skeleton.Input size="small" style={{ width: '150px' }} />
          </Row>
        }
        width="100%"
      />
    </InfoCartWrapper>
    <Space height={18} />
    <Text color="primary" size="xs" weight="medium">
      <Skeleton.Input size="small" style={{ width: '50px' }} />
    </Text>
    <Space height={12} />
    <Row>
      <Column>
        <Row>
          <Skeleton.Avatar
            style={{ width: 32, height: 32, borderRadius: '6px' }}
          />
          <Space width={12} />
          <Column>
            <Skeleton.Input size="small" style={{ width: '150px' }} />
            <Skeleton.Input size="small" style={{ width: '150px' }} />
          </Column>
        </Row>
        <Space height={18} />
        <Row>
          <Skeleton.Avatar
            style={{ width: 32, height: 32, borderRadius: '6px' }}
          />
          <Space width={12} />
          <Column>
            <Skeleton.Input size="small" style={{ width: '150px' }} />
            <Skeleton.Input size="small" style={{ width: '150px' }} />
          </Column>
        </Row>
      </Column>
      <Column>
        <Row>
          <Skeleton.Avatar
            style={{ width: 32, height: 32, borderRadius: '6px' }}
          />
          <Space width={12} />
          <Column>
            <Skeleton.Input size="small" style={{ width: '150px' }} />
            <Skeleton.Input size="small" style={{ width: '150px' }} />
          </Column>
        </Row>
        <Space height={18} />
        <Row>
          <Skeleton.Avatar
            style={{ width: 32, height: 32, borderRadius: '6px' }}
          />
          <Space width={12} />
          <Column>
            <Skeleton.Input size="small" style={{ width: '150px' }} />
            <Skeleton.Input size="small" style={{ width: '150px' }} />
          </Column>
        </Row>
      </Column>
    </Row>
  </>
);

export const IconWrapper = styled(Row)`
  width: 32px;
  height: 32px;
  min-width: 32px;
  position: relative;

  .phone-out-line {
    transform: rotate(90deg);
  }

  svg {
    width: 16px;
    height: 16px;
  }

  border-radius: 6px;

  margin: 0px;

  svg {
    path {
      fill: ${props => props.theme.colors.primary.main};
    }
  }

  &::before {
    content: ' ';
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: ${props => props.theme.colors.primary.main};
    opacity: 0.2;
    border-radius: 6px;
  }
`;

const InfoCartWrapper = styled.div`
  padding: ${p => p.theme.app.padding.horizontal} 0px;
  border-bottom: 1px solid #f1f1f1;

  .ant-popover-content {
    width: 264px;
  }
`;

const AvatarWrapper = styled(Row)`
  height: 64px;
  min-width: 64px;
  width: 64px;
  border-radius: 999px;
  margin-right: 6px;

  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
`;
